import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { WaitlistApiClient } from 'api/waitlist';
import WaitlistModel from 'models/Waitlist/Waitlist';
import { WaitlistStatus } from 'models/Waitlist/types';

interface RejectWaitlistPayload {
  waitlistId: string;
  reason?: string;
}

export function useWaitlistsByStatus(status: WaitlistStatus) {
  return useQuery<WaitlistModel[]>({
    queryKey: ['waitlists', 'status', status],
    queryFn: () => WaitlistApiClient.getWaitlistsByStatus(status),
    enabled: !!status
  });
}

export function useApproveWaitlist() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<string, Error, string>({
    mutationFn: (waitlistId: string) => WaitlistApiClient.approveWaitlist(waitlistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlists'] });
      toast({
        title: 'Waitlist entry approved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to approve waitlist entry',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}

export function useRejectWaitlist() {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<string, Error, RejectWaitlistPayload>({
    mutationFn: ({ waitlistId }: RejectWaitlistPayload) =>
      WaitlistApiClient.rejectWaitlist(waitlistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlists'] });
      toast({
        title: 'Waitlist entry rejected',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to reject waitlist entry',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  });
}
